import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Calendar, User, ArrowLeft, BookOpen } from "lucide-react";
import HTMLContent from "@/components/HTMLContent";
import ShareButtons from "@/components/ShareButtons";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image_url: string;
  created_at: string;
  author_id: string;
}

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchPost(id);
    }
  }, [id]);

  const fetchPost = async (postId: string) => {
    try {
      const { data, error } = await supabase
        .from('blog_posts_2025_11_10_14_11')
        .select('*')
        .eq('id', postId)
        .eq('is_published', true)
        .single();

      if (error) throw error;
      setPost(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo cargar el post del blog",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-8"></div>
          <div className="h-12 bg-muted rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-muted rounded w-1/2 mb-8"></div>
          <div className="h-64 bg-muted rounded mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-4">Post no encontrado</h1>
        <p className="text-muted-foreground mb-8">
          El post que buscas no existe o ha sido eliminado.
        </p>
        <Link to="/blog">
          <Button>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back button */}
      <Link to="/blog" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver al Blog
      </Link>

      {/* Post header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          {post.title}
        </h1>
        <div className="flex items-center text-muted-foreground mb-6">
          <Calendar className="h-4 w-4 mr-2" />
          {formatDate(post.created_at)}
          <User className="h-4 w-4 ml-4 mr-2" />
          Amelba Team
        </div>
        {post.excerpt && (
          <p className="text-xl text-muted-foreground leading-relaxed">
            {post.excerpt}
          </p>
        )}
      </div>

      {/* Featured image */}
      {post.image_url && (
        <div className="mb-8">
          <img 
            src={post.image_url} 
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-lg"
          />
        </div>
      )}

      {/* Post content */}
      <HTMLContent 
        content={post.content} 
        className="prose-lg text-foreground"
      />

      {/* Share and navigation */}
      <div className="mt-12 pt-8 border-t">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="w-full">
            <ShareButtons 
              url={window.location.href}
              title={post.title}
              description={post.excerpt || post.content.replace(/<[^>]*>/g, '').substring(0, 160) + '...'}
              className="text-sm"
            />
          </div>
          <Link to="/blog">
            <Button variant="outline">
              Ver más posts
            </Button>
          </Link>
        </div>
      </div>

      {/* Related posts CTA */}
      <div className="mt-16 text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">¿Te gustó este artículo?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Explora más contenido en nuestro blog y mantente al día con las últimas 
          tendencias en streaming y producción audiovisual.
        </p>
        <Link to="/blog">
          <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:opacity-90">
            Explorar más artículos
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default BlogPost;